// (Copyright) Confluent, Inc.

import React, { type ReactNode, useEffect, useState } from 'react';
import styled, { css, type CSSProp, ThemeProvider } from 'styled-components';
import { usePopper } from 'react-popper';
import { type Placement } from '@popperjs/core/lib';

import { Portal } from '../Portal';
import { createTheme } from '../../utils/theme';
import { analytics } from '../../../utils/analytics';

/* eslint-disable rulesdir/no-css-var-colors */
const StyledPopper = styled.div<any>`
  z-index: ${({ showAboveModals }) =>
    showAboveModals ? 'var(--POPOVER_INSIDE_MODAL_ZINDEX)' : 'var(--POPOVER_ZINDEX)'};
  transition: opacity 0s 0.1s ease;
  font-size: 14px;
  line-height: 22px;
  background-color: ${({ backgroundColor }) => backgroundColor ?? 'var(--DIM_DARK)'};
  color: var(--WHITE);
  padding: ${({ noPadding }) => (noPadding ? '0' : '6px 12px')};
  border-radius: 4px;
  ${({ width }) =>
    width
      ? css`
          width: ${width}px;
        `
      : ''};
  ${({ maxTipWidth }) =>
    maxTipWidth
      ? css`
          max-width: ${maxTipWidth}px;
        `
      : ''};

  box-shadow: var(--SHADOW_HOVER);
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};

  a {
    color: var(--WHITE);
    text-decoration: underline;
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

const InnerTip = styled.span`
  position: relative;
`;

export type PopoverPlacement = Placement;

type PopoverProps = {
  backgroundColor?: string;
  body: ReactNode;
  children?: ReactNode;
  className?: string;
  contentWrapperStyle?: unknown;
  css?: CSSProp;
  darkMode?: boolean;
  disabled?: boolean;
  inline?: boolean;
  innerTipStyle?: unknown;
  isOpen: boolean;
  maxTipWidth?: number;
  noPadding?: boolean;
  onClickOutside?: () => void;
  onMouseEnterCb: () => void;
  onMouseLeaveCb: () => void;
  popoverId?: string;
  preferPlace?: null | PopoverPlacement;
  showAboveModals: boolean;
  style?: unknown;
  tipBody?: ReactNode;
  width?: number;
};

const Popover = (props: PopoverProps) => {
  const {
    backgroundColor,
    body,
    children,
    innerTipStyle,
    isOpen,
    popoverId,
    onClickOutside,
    onMouseEnterCb,
    onMouseLeaveCb,
    preferPlace,
    width: _,
    ...popoverProps
  } = props;

  const [referenceElement, setReferenceElement] = useState<Element | null | undefined>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null | undefined>(null);

  useEffect(() => {
    if (popoverId != null && isOpen) {
      analytics.track({ name: 'Popover Shown', properties: { popoverId } });
    }

    return () => {
      if (popoverId != null && isOpen) {
        analytics.track({ name: 'Popover Hidden', properties: { popoverId } });
      }
    };
  }, [popoverId, isOpen]);

  // Handle clicks outside the popover if onClickOutside has been set
  useEffect(() => {
    if (!onClickOutside) {
      return;
    }

    const handleClickOutside: EventListener = (event) => {
      const target = event.target;
      if (
        target instanceof Node &&
        referenceElement != null &&
        popperElement != null &&
        !referenceElement.contains(target) &&
        !popperElement.contains(target)
      ) {
        onClickOutside();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClickOutside, popperElement, referenceElement]);

  const { styles } = usePopper(referenceElement, popperElement, {
    placement: preferPlace ?? 'top',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 12,
        },
      },
    ],
  });

  const themedBody = React.useMemo(() => {
    if (backgroundColor == null) {
      return <ThemeProvider theme={createTheme('V1_DARK')}>{body}</ThemeProvider>;
    } else {
      return body;
    }
  }, [backgroundColor, body]);

  return (
    <>
      {isOpen && (
        <Portal>
          <StyledPopper
            isOpen={isOpen}
            onMouseEnter={onMouseEnterCb}
            onMouseLeave={onMouseLeaveCb}
            ref={setPopperElement}
            style={styles.popper}
            {...popoverProps}
          >
            {themedBody}
          </StyledPopper>
        </Portal>
      )}
      <InnerTip ref={setReferenceElement} style={innerTipStyle}>
        {children}
      </InnerTip>
    </>
  );
};

Popover.defaultProps = {
  onMouseEnterCb: () => {},
  onMouseLeaveCb: () => {},
  showAboveModals: false,
};

export default Popover;
